// Type-only: loads @tiptap/markdown's Editor augmentation (editor.markdown).
import type { Editor } from '@tiptap/core';
import type {} from '@tiptap/markdown';
import { Fragment, Slice } from '@tiptap/pm/model';
import type { EditorView } from '@tiptap/pm/view';

/**
 * Typed safe-failure envelope for the pure paste parser (OpenSpec
 * `monorepo-kit-editor` tasks 4.3/4.10; design D8). The only fields that may
 * cross the reporting seam: a stable code, the error class name, and the input
 * length. Never the pasted text and never a raw error message — either could
 * carry PHI to an unredacted transport.
 */
export interface PasteParseFailure {
  code: 'paste_parse_failed';
  /** `Error` class name, or `typeof` for non-Error throws — never the message. */
  errorName: string;
  /** Length of the pasted text — never the text. */
  textLength: number;
}

export type PasteParseFailureReporter = (failure: PasteParseFailure) => void;

/**
 * Pure core of the pasted-markdown parse (the 5.5 `/core` move candidate).
 * Parses pasted plain text through the canonical markdown pipeline (the kit's
 * pinned ATX-only marked instance, via `editor.markdown.parse`) so GFM tables,
 * lists, headings — and the clipboard extension's own copy output — paste as
 * structured content. Plain prose still lands as plain paragraphs.
 *
 * `openIsolating: false` keeps sections closed in the slice — defensive;
 * schema fitting re-closes them on insert anyway. Returns `null` on empty or
 * unparseable input, which lets ProseMirror's default plain-text paste run
 * instead of a broken paste.
 *
 * The reporter is REQUIRED so this module stays free of application logging
 * (the purity contract `/core` will enforce); the app-side wrapper in
 * `paste.ts` supplies the logger adapter and preserves the frozen warn trace.
 */
export const parsePastedMarkdown = (
  editor: Editor,
  view: EditorView,
  text: string,
  onParseFailure: PasteParseFailureReporter
): Slice | null => {
  try {
    if (!editor.markdown) {
      throw new Error('markdown extension missing');
    }
    const doc = editor.markdown.parse(text);
    if (!doc.content?.length) {
      return null;
    }
    const fragment = Fragment.fromJSON(view.state.schema, doc.content);
    return Slice.maxOpen(fragment, false);
  } catch (error) {
    onParseFailure({
      code: 'paste_parse_failed',
      errorName: error instanceof Error ? error.name : typeof error,
      textLength: text.length,
    });
    return null;
  }
};
